import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import AlternatingIcons from '../components/icons/AlternatingIcons'
import { ICONS, MOBILE_SECOND } from '../components/icons/registry'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import Breadcrumbs from '../components/Breadcrumbs'
import { generateServiceSchema, generateLocalBusinessSchema, generateFAQSchema } from '../lib/structured-data-utils'
import CtaSection from '../components/CtaSection'

const SoftwareDevelopmentDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  const services = [
    {
      id: 'custom-web-applications',
      title: 'Custom Web Applications',
      description: 'Build powerful, scalable web applications tailored to your business needs. From single-page applications to complex enterprise platforms, we deliver high-performance web solutions using modern frameworks and best practices.',
      icon: <ICONS.webApplications className="w-full h-full" darkTreatment="none" />,
      features: [
        'Progressive Web App (PWA) Development',
        'Real-time Data Dashboards & Portals',
        'E-commerce & Marketplace Platforms',
        'Content Management Systems (CMS)',
        'SaaS Product Development',
        'Performance Optimization & Caching'
      ]
    },
    {
      id: 'mobile-app-development',
      title: 'Mobile App Development',
      description: 'Create native and cross-platform mobile applications that deliver exceptional user experiences. We build apps for iOS and Android using Flutter, Swift, and React Native to reach your customers on every device.',
      // Two platforms on one card, so both show in turn rather than one standing in
      // for the pair. Same component and reason as the About technology cards.
      icon: (
        <AlternatingIcons
          className="w-full h-full"
          first={<ICONS.mobile className="w-full h-full" darkTreatment="none" />}
          second={<MOBILE_SECOND className="w-full h-full" darkTreatment="none" />}
        />
      ),
      features: [
        'Cross-platform App Development (Flutter)',
        'Native iOS Development (Swift)',
        'Native Android Development (Kotlin)',
        'App Store Optimization & Submission',
        'Push Notifications & Real-time Features',
        'Offline-first Architecture'
      ]
    },
    {
      id: 'enterprise-software-solutions',
      title: 'Enterprise Software Solutions',
      description: 'Design and develop enterprise-grade software that streamlines operations, improves productivity, and drives digital transformation. We build robust solutions that integrate with your existing systems and scale with your business.',
      icon: <ICONS.enterpriseSoftware className="w-full h-full" darkTreatment="none" />,
      features: [
        'ERP & CRM System Development',
        'Workflow Automation Platforms',
        'Business Intelligence & Reporting',
        'Legacy System Modernization',
        'Microservices Architecture Design',
        'Role-based Access Control & Security'
      ]
    },
    {
      id: 'api-development-integration',
      title: 'API Development & Integration',
      description: 'Build robust APIs and integrate third-party services to create connected, data-driven applications. We design RESTful and GraphQL APIs that are secure, well-documented, and built for performance at scale.',
      icon: <ICONS.api className="w-full h-full" darkTreatment="none" />,
      features: [
        'RESTful API Design & Development',
        'GraphQL API Implementation',
        'Third-party API Integration',
        'API Gateway & Rate Limiting',
        'Webhook & Event-driven Architecture',
        'API Documentation & Developer Portals'
      ]
    }
  ]

  const tools = [
    { name: 'React', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'TypeScript', category: 'Language' },
    { name: 'Python', category: 'Language' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Docker', category: 'Containers' },
    { name: 'PostgreSQL', category: 'Database' },
    { name: 'MongoDB', category: 'Database' },
    { name: 'Flutter', category: 'Mobile' },
    { name: 'Swift', category: 'Mobile' },
    { name: 'GraphQL', category: 'API' },
    { name: 'REST APIs', category: 'API' },
    { name: 'Redis', category: 'Cache' },
    { name: 'Kubernetes', category: 'Orchestration' }
  ]

  const benefits = [
    {
      title: 'Projects Delivered',
      description: 'Successfully delivered custom software projects across diverse industries and business domains.',
      stat: '200+'
    },
    {
      title: 'Enterprise Clients',
      description: 'Trusted by leading enterprises in Dubai and the UAE for mission-critical software solutions.',
      stat: '50+'
    },
    {
      title: 'Uptime Guarantee',
      description: 'Our solutions are built for reliability with robust architecture and proactive monitoring.',
      stat: '99.9%'
    },
    {
      title: 'Client Satisfaction',
      description: 'Consistently rated highly by our clients for quality, communication, and on-time delivery.',
      stat: '4.9/5'
    }
  ]

  const faqs = [
    {
      question: 'What software development services do you offer in Dubai?',
      answer: 'We offer full-stack software development services in Dubai including custom web application development with React and Node.js, mobile app development with Flutter and Swift, enterprise software solutions, API development and integration, SaaS product development, and legacy system modernization. We serve startups, SMEs, and enterprises across the UAE.'
    },
    {
      question: 'How much does custom software development cost in Dubai?',
      answer: 'Custom software development costs in Dubai vary based on complexity and scope. A basic web application starts from AED 40,000 to AED 80,000, while a full enterprise platform ranges from AED 150,000 to AED 500,000. Mobile app development typically costs AED 50,000 to AED 200,000 depending on platform and features. We offer fixed-price projects, time and materials, and dedicated team models to fit your budget.'
    },
    {
      question: 'How long does it take to build a custom software application?',
      answer: 'Development timelines depend on the scope and complexity of the project. A minimum viable product (MVP) typically takes 6 to 10 weeks. A full-featured web application takes 3 to 6 months, while complex enterprise platforms may require 6 to 12 months. We use agile methodology with two-week sprints so you see working software from the first sprint and can provide feedback throughout the process.'
    },
    {
      question: 'What technologies and frameworks do you use for software development?',
      answer: 'We use modern, proven technologies including React, Next.js, and Vue.js for frontend development, Node.js, Python, and Go for backend services, Flutter and Swift for mobile apps, PostgreSQL, MongoDB, and Redis for databases, and AWS for cloud infrastructure. We select the technology stack based on your project requirements, team expertise, and long-term maintainability rather than following trends.'
    },
    {
      question: 'Do you build mobile apps for both iOS and Android?',
      answer: 'Yes, we build mobile applications for both iOS and Android. We use Flutter for cross-platform development which allows us to build a single codebase that runs natively on both platforms, reducing development time and cost by up to 40%. For projects requiring maximum platform-specific performance, we also offer native iOS development with Swift and native Android development with Kotlin.'
    },
    {
      question: 'Can you modernize our legacy software systems?',
      answer: 'Yes, legacy system modernization is one of our core services. We help Dubai businesses migrate from outdated technologies to modern architectures through incremental refactoring, API wrapping of legacy systems, database migration, and re-platforming to cloud-native infrastructure. Our approach minimizes business disruption by modernizing systems in phases rather than a risky big-bang replacement.'
    },
    {
      question: 'What industries do you serve with software development in Dubai?',
      answer: 'We serve a wide range of industries in Dubai and the UAE including fintech and banking, e-commerce and retail, healthcare, logistics and supply chain, real estate and proptech, education, and government. Our software solutions are built to meet industry-specific compliance requirements such as PCI DSS for payment processing and data residency regulations for UAE-based businesses.'
    },
    {
      question: 'Do you provide ongoing support and maintenance after launch?',
      answer: 'Yes, we offer comprehensive post-launch support packages including bug fixes, security updates, performance monitoring, feature enhancements, and infrastructure management. Our maintenance retainers start from AED 5,000 per month and include SLA-backed response times. Many of our Dubai clients start with a development project and transition to ongoing support to ensure their software remains secure, performant, and up to date.'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  }

  const serviceSchemas = services.map(service => generateServiceSchema({
    name: `${service.title} in Dubai`,
    description: service.description,
    provider: 'Demirix',
    areaServed: 'Dubai, UAE'
  }))

  const localBusinessSchema = generateLocalBusinessSchema()
  const faqSchema = generateFAQSchema(faqs)
  const structuredData = [...serviceSchemas, localBusinessSchema, faqSchema]

  const breadcrumbs = [
    { name: 'Services', path: '/services' },
    { name: 'Software Development Dubai', path: '/software-development-dubai' }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="Software Development Company in Dubai | Custom Software Solutions UAE"
        description="Leading software development company in Dubai. We build custom web applications, mobile apps, enterprise software, and API integrations. Transform your business with tailored software solutions from Demirix."
        keywords={[
          'software development company in dubai',
          'software development dubai',
          'custom software development dubai',
          'software company dubai',
          'web application development dubai',
          'mobile app development dubai',
          'enterprise software dubai',
          'api development dubai',
          'software solutions uae',
          'custom software uae',
          'app development company dubai',
          'software engineering dubai'
        ]}
        url={currentUrl}
        structuredData={structuredData}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary-100 dark:bg-primary-900/20 rounded-full blur-3xl opacity-50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
              Software Development
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Software Development Company in Dubai
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
              Build custom software solutions that drive your Dubai business forward. From web and mobile
              applications to enterprise platforms and API integrations, we deliver scalable, high-performance
              software tailored to your unique requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <CalendlyButton className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto">
                Schedule Software Consultation
              </CalendlyButton>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto"
              >
                Get a Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits/Stats Section */}
      <section className="section-padding bg-primary-600 dark:bg-primary-700">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div key={index} variants={itemVariants} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {benefit.stat}
                </div>
                <div className="text-lg font-semibold text-white mb-1">
                  {benefit.title}
                </div>
                <div className="text-primary-100 text-sm">
                  {benefit.description}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Our Software Development Services
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                End-to-end software development solutions designed for Dubai businesses looking to
                build custom applications and digital products that scale.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service) => (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                      {service.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-3">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 dark:text-gray-200">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Tools & Technologies We Use
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                We leverage modern technologies and frameworks to build robust, scalable software solutions.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-4">
              {tools.map((tool, index) => (
                <div
                  key={index}
                  className="px-6 py-3 bg-white dark:bg-gray-700 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="font-medium text-gray-900 dark:text-white">{tool.name}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{tool.category}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Common questions about software development services in Dubai answered by our engineering team.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-100 dark:border-gray-700"
                >
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cross-link to other services */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Related Services
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
              Explore our other specialized technology services for Dubai businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/dubai-services"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                Dubai Services
              </Link>
              <Link
                to="/devops-services-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                DevOps Services
              </Link>
              <Link
                to="/aws-cloud-consulting-dubai"
                className="inline-flex items-center px-6 py-3 border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 hover:bg-primary-600 hover:text-white dark:hover:bg-primary-400 dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200"
              >
                AWS Cloud Consulting
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection
        heading="Ready to Build Your Next Software Solution?"
        description="Let's discuss how custom software can transform your business operations and help your Dubai company achieve its digital transformation goals."
        primary={{ kind: 'calendly', label: 'Schedule Free Consultation' }}
        secondary={{ kind: 'link', label: 'Contact Us', href: '/contact' }}
      />
    </div>
  )
}

export default SoftwareDevelopmentDubai
